// public/url-parser.js
// Pure functions for URL parsing - no side effects, easily testable

import { SHORT_CODES } from './config.js';

const CODE_BY_SOURCE_TYPE = Object.fromEntries(
  Object.entries(SHORT_CODES).map(([code, { source, type }]) => [`${source}_${type}`, code])
);

/**
 * The short path ('/mfb.abc123') for a parsed { source, type, id }: the inverse
 * of the short-code branch of extractSourceInfo.
 * @param {{ source: string, type: string, id: string }} sourceInfo
 * @returns {string|null} - null when no short code names the pair, or no id
 */
export function toShortPath({ source, type, id }) {
  const code = CODE_BY_SOURCE_TYPE[`${source}_${type}`];
  return code && id ? `/${code}.${id}` : null;
}

/**
 * Extracts source information from a deck/binder URL
 * @param {string} url - The URL to parse
 * @returns {Object|null} - { source, type, id } or null if invalid
 */
export function extractSourceInfo(url) {
  try {
    if (!url) return null;

    // Short codes (e.g., "mfc.abc123", "/mfb.xxx"): the one codec table
    // Handle optional leading slash (e.g., "/mfb.xxx" or "mfb.xxx")
    const simpleMatch = url.match(/^\/?([a-z]+)\.(.+)$/);
    if (simpleMatch) {
      const [, code, id] = simpleMatch;
      if (SHORT_CODES[code]) {
        return { ...SHORT_CODES[code], id };
      }
    }

    const fullUrl = url.startsWith('http') ? url : `https://${url}`;
    const urlObj = new URL(fullUrl);
    const hostname = urlObj.hostname.replace('www.', '');
    const pathSegments = urlObj.pathname.split('/').filter(Boolean);

    if (hostname === 'moxfield.com') {
      const decksIndex = pathSegments.findIndex((s) => s === 'decks');
      const bindersIndex = pathSegments.findIndex((s) => s === 'binders');
      const collectionIndex = pathSegments.findIndex(
        (s) => s === 'collection' || s === 'collections'
      );
      if (decksIndex !== -1 && pathSegments.length > decksIndex + 1) {
        return { source: 'moxfield', type: 'deck', id: pathSegments[decksIndex + 1] };
      }
      if (bindersIndex !== -1 && pathSegments.length > bindersIndex + 1) {
        return { source: 'moxfield', type: 'binder', id: pathSegments[bindersIndex + 1] };
      }
      if (collectionIndex !== -1 && pathSegments.length > collectionIndex + 1) {
        return { source: 'moxfield', type: 'collection', id: pathSegments[collectionIndex + 1] };
      }
    } else if (hostname === 'scryfall.com') {
      const decksIndex = pathSegments.findIndex((s) => s === 'decks');
      if (
        decksIndex > 0 &&
        pathSegments.length > decksIndex + 1 &&
        pathSegments[decksIndex - 1]?.startsWith('@')
      ) {
        return { source: 'scryfall', type: 'deck', id: pathSegments[decksIndex + 1] };
      }
    } else if (hostname === 'cubecobra.com') {
      // /cube/{overview|list|playtest|...}/{id} (binder_urls.py's pattern)
      if (pathSegments[0] === 'cube' && /^[a-z]+$/.test(pathSegments[1] || '')) {
        const id = pathSegments[2];
        if (id && /^[a-zA-Z0-9_-]+$/.test(id)) return { source: 'cubecobra', type: 'cube', id };
      }
    } else if (hostname === 'manabox.app') {
      // Public deck share link: /decks/{shareId}
      if (pathSegments[0] === 'decks' && pathSegments[1]) {
        return { source: 'manabox', type: 'deck', id: pathSegments[1] };
      }
    } else if (hostname === 'archidekt.com') {
      // Check for deck URLs: /decks/{id}
      const decksIndex = pathSegments.findIndex((s) => s === 'decks');
      if (decksIndex !== -1 && pathSegments.length > decksIndex + 1) {
        const potentialId = pathSegments[decksIndex + 1];
        if (/^\d+$/.test(potentialId)) {
          return { source: 'archidekt', type: 'deck', id: potentialId };
        }
      }

      // Check for collection URLs: /collection/{userId}, /collection/v2/{userId}, or /u/{username}/collection
      const collectionIndex = pathSegments.findIndex((s) => s === 'collection');
      if (collectionIndex !== -1) {
        // Look for numeric user ID anywhere after 'collection' in the path
        // Handles: /collection/{userId}, /collection/v2/{userId}, etc.
        const segmentsAfterCollection = pathSegments.slice(collectionIndex + 1);
        const userId = segmentsAfterCollection.find((s) => /^\d+$/.test(s));
        if (userId) {
          return { source: 'archidekt', type: 'collection', id: userId };
        }

        // Format: /u/{username}/collection - username segment contains the user ID
        const uIndex = pathSegments.findIndex((s) => s === 'u');
        if (uIndex !== -1 && uIndex < collectionIndex && pathSegments.length > uIndex + 1) {
          const userSegment = pathSegments[uIndex + 1];
          // User segment might be numeric ID or username
          // We'll pass it along and let the backend resolve it
          return { source: 'archidekt', type: 'collection', id: userSegment };
        }
      }
    }
  } catch (e) {
    console.error('Error parsing URL:', e);
    return null;
  }
  return null;
}

/**
 * `extractSourceInfo`, for a URL that may open as a binder: null for anything
 * else, including a cube. A cube is only ever a Project's source (its
 * cards are wanted, not offered); opening it as a binder would misrepresent
 * it (CONTEXT.md "Source Link").
 * @param {string} url
 * @returns {Object|null} - { source, type, id } or null
 */
export function extractBinderSourceInfo(url) {
  const info = extractSourceInfo(url);
  return info && info.type !== 'cube' ? info : null;
}

/**
 * Convert recent binder entries (with URLs) to binder key objects for the search API.
 * @param {Array<{url: string}>} recentBinders
 * @returns {Array<{source: string, sourceType: string, sourceId: string}>}
 */
export function buildBinderKeys(recentBinders) {
  const keys = [];
  for (const binder of recentBinders) {
    if (!binder.url) continue;
    const info = extractSourceInfo(binder.url);
    if (info) {
      keys.push({
        source: info.source,
        sourceType: info.type,
        sourceId: info.id,
      });
    }
  }
  return keys;
}

/**
 * Generates a cache key from source info
 * @param {Object} sourceInfo - { source, type, id }
 * @param {string} prefix - Cache key prefix
 * @returns {string|null} - Cache key or null if invalid
 */
export function getCacheKeyFromSourceInfo(sourceInfo, prefix) {
  if (sourceInfo?.source && sourceInfo?.type && sourceInfo?.id) {
    return `${prefix}${sourceInfo.source}_${sourceInfo.type}_${sourceInfo.id}`;
  }
  return null;
}
